/**
 * Mapeamento puro do historico de consorcio (linhas da view
 * comissao-consorcio) para o shape consumido por calcularMetasColaboradores
 * (ver metaMediaUtils.js).
 *
 * A rota devolve uma linha por colaborador / empresa / competencia / status /
 * tipo_dado / tipo_evento, e o filtro generico da API aplica IN em cada
 * coluna independentemente. Duas armadilhas nascem dai e este mapeamento
 * existe para fecha-las:
 *
 * 1. Produto cartesiano quando os meses pedidos atravessam o ano. Pedir
 *    nov/2025, dez/2025 e jan/2026 vira `ano_ref IN (2025,2026)` e
 *    `mes_ref IN (11,12,1)` no filtro da API — que tambem devolve jan/2025 e
 *    nov/2026. Descartamos aqui toda linha cujo par (ano_ref, mes_ref) nao
 *    estava exatamente na lista de meses pedidos.
 *
 * 2. Mais de uma linha por colaborador na mesma competencia. A view agrupa
 *    tambem por id_empresa, id_tipo_dado e id_tipo_evento: um colaborador com
 *    movimento em duas empresas no mesmo mes devolve duas linhas. Somamos o
 *    qtde de todas as linhas do mesmo par (colaborador, ano, mes) — pegar so
 *    a primeira perderia metade do historico dele.
 */

/**
 * Chave de agrupamento, com ano e mes NORMALIZADOS para numero.
 *
 * Os meses pedidos nascem de Date (numeros: 2026, 7). A resposta vem do Oracle
 * pela API e chega como string, as vezes com zero a esquerda ("2026", "07").
 * Sem normalizar, "2026-7" nunca casa com "2026-07" e TODA linha e descartada —
 * a media zera para todo mundo e as metas caem no piso, sem erro nenhum.
 *
 * id_colaborador vai por String() porque ali o que importa e so a igualdade
 * textual, e ele nao tem zero a esquerda.
 */
function chaveColaboradorMes(idColaborador, anoRef, mesRef) {
  return `${String(idColaborador)}-${Number(anoRef)}-${Number(mesRef)}`;
}

/**
 * Mesma normalizacao para o par (ano, mes) usado na guarda do cartesiano.
 */
function chaveCompetencia(anoRef, mesRef) {
  return `${Number(anoRef)}-${Number(mesRef)}`;
}

/**
 * @param {Array<{id_colaborador, ano_ref?, mes_ref?, ano?, mes?, qtde?}>} linhas resposta crua da API
 * @param {Array<{ano_ref, mes_ref}>} meses meses pedidos, na ordem pedida
 * @param {Array<number|string>} idColaboradores colaboradores pedidos, na ordem pedida
 * @returns {Array<{id_colaborador, meses: Array<{ano_ref, mes_ref, quantidade}>}>}
 */
/**
 * Competencia de uma linha da resposta — a do DADO, nao a do fechamento.
 *
 * ARMADILHA: dados-consorcio devolve as DUAS, e elas sao diferentes.
 *
 *   "mes_ref": "5", "ano_ref": "2026"   competencia do FECHAMENTO
 *   "mes":     "4", "ano":     "2026"   competencia do DADO (data_venda em abril)
 *
 * O fechamento de maio carrega o dado de abril — e o retroativo. A media conta a
 * venda pelo mes em que ela ACONTECEU, entao vale `ano`/`mes`. Ler `ano_ref`
 * classifica a venda de abril como maio, nenhum par casa com o que foi pedido e
 * TODA linha e descartada: media zero, metas no piso, sem erro nenhum.
 *
 * O fallback para `_ref` existe para a rota de comissao, que so tem esses nomes.
 * A ordem e que importa: `ano`/`mes` primeiro.
 * @param {Object} linha
 * @returns {{ano: *, mes: *}}
 */
function competenciaDaLinha(linha) {
  const l = linha || {};
  return {
    ano: l.ano != null ? l.ano : l.ano_ref,
    mes: l.mes != null ? l.mes : l.mes_ref
  };
}

/**
 * Quanto uma linha da resposta vale na contagem de cotas.
 *
 * A rota da media (/campanhas/dados-consorcio) devolve a VENDA, uma linha por
 * contrato — nesse formato cada linha vale 1. A rota de comissao, que esta
 * funcao tambem ja atendeu, devolve agregado com a coluna `qtde`.
 *
 * Ler `qtde` quando existe e cair em 1 quando nao existe cobre as duas sem
 * adivinhar: se a origem passar a informar quantas cotas ha no contrato, o
 * numero vem de `qtde` e nao de uma contagem de linhas.
 *
 * O contrario — assumir `qtde` sempre — zeraria a media inteira em silencio no
 * dia em que a rota nao trouxesse a coluna.
 * @param {Object} linha
 * @returns {number}
 */
function quantidadeDaLinha(linha) {
  if (linha == null) return 0;
  if (linha.qtde == null || linha.qtde === "") return 1;
  return Number(linha.qtde) || 0;
}

export function mapearHistoricoConsorcio(linhas, meses, idColaboradores) {
  const mesesPedidos = meses || [];
  const colaboradoresPedidos = idColaboradores || [];

  const paresPedidos = new Set(mesesPedidos.map(m => chaveCompetencia(m.ano_ref, m.mes_ref)));

  const somaPorChave = new Map();
  (linhas || []).forEach(linha => {
    const { ano, mes } = competenciaDaLinha(linha);
    if (!paresPedidos.has(chaveCompetencia(ano, mes))) return;

    const chave = chaveColaboradorMes(linha.id_colaborador, ano, mes);
    const acumulado = somaPorChave.get(chave) || 0;
    somaPorChave.set(chave, acumulado + quantidadeDaLinha(linha));
  });

  return colaboradoresPedidos.map(idColaborador => ({
    id_colaborador: idColaborador,
    meses: mesesPedidos.map(m => ({
      ano_ref: m.ano_ref,
      mes_ref: m.mes_ref,
      quantidade: somaPorChave.get(chaveColaboradorMes(idColaborador, m.ano_ref, m.mes_ref)) || 0
    }))
  }));
}

/**
 * Soma valor_comissao por colaborador para a comissao P2 (fechada) de uma
 * unica competencia — usada para mostrar quanto o bonus por "% sobre
 * referencia" da meta do participante daria hoje.
 *
 * Mesma armadilha 2 de mapearHistoricoConsorcio: a view agrupa tambem por
 * id_empresa, entao um colaborador com movimento em duas empresas no mesmo
 * mes devolve duas linhas — somamos as duas. Nao existe a armadilha 1
 * (produto cartesiano de meses) aqui porque o chamador pede uma unica
 * competencia (ano_ref/mes_ref), nunca uma lista.
 *
 * Diferente de mapearHistoricoConsorcio, colaborador sem nenhuma linha na
 * resposta fica ausente do mapa — nao entra com 0. Ausencia de comissao no
 * periodo nao e o mesmo que comissao zero: quem consome o mapa
 * (TabelaMetasCalculadas) precisa distinguir os dois casos para mostrar um
 * travessao em vez de "R$ 0,00".
 * @param {Array<{id_colaborador, valor_comissao}>} linhas resposta crua da API
 * @returns {Object.<string, number>} chave id_colaborador (string) -> soma de valor_comissao
 */
export function mapearComissaoP2Consorcio(linhas) {
  const somaPorColaborador = {};
  (linhas || []).forEach(linha => {
    const chave = String(linha.id_colaborador);
    const valor = Number(linha.valor_comissao) || 0;
    somaPorColaborador[chave] = (somaPorColaborador[chave] || 0) + valor;
  });
  return somaPorColaborador;
}
